/**
 * Visible accountant identity at the top of every page.
 *
 * Per plan.md §3.1 trust-signals: the audience is uploading personal financial
 * documents to a website they've never seen. They need to *see* who this is —
 * "this is Sarah's place, not some random site" — before any UI element asks
 * them to act.
 */

import { t } from "@dovach/i18n";

interface TrustHeaderProps {
  accountantName: string;
  /** Optional accountant logo URL (uploaded via dashboard, served from collect.dovach.com). */
  logoUrl?: string;
}

export function TrustHeader({ accountantName, logoUrl }: TrustHeaderProps) {
  return (
    <header className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="h-12 w-12 rounded-xl object-cover"
          width={48}
          height={48}
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600 font-semibold text-white"
        >
          {accountantName.trim().charAt(0)}
        </div>
      )}
      <div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t("client_web.trust_header", { accountant_name: "" }).replace("{{accountant_name}}", "")}
        </p>
        <p className="text-lg font-semibold tracking-tight">{accountantName}</p>
      </div>
    </header>
  );
}
